import { SagaIterator } from 'redux-saga';
import { call, all, takeLatest, put, select } from 'redux-saga/effects';
import i18next from 'i18next';
import ddt from '@wix/da-ddt';
import { XhrResponse, isApiError, requestPuppy } from '@wix/da-http-client';
import { actions as modalActions } from '@wix/da-shared-react/pkg/Modals';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/basicActions';
import { withOffset } from '@wix/da-shared-react/pkg/Stream';
import { fetchNext } from '@wix/da-shared-react/pkg/Stream/base/actions';
import { FetchType } from '@wix/da-shared-react/pkg/Stream/base/types';
import { logBiEvent } from '@wix/da-shared-react/pkg/biLogger/redux/saga';
import { getPageBiContext } from '@wix/da-shared-react/pkg/biLogger/redux/selectors';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';

import {
  PapiDacheckoutCreditcardDelete,
  PapiDacheckoutOrders,
  PapiDacheckoutSubscriptionsCancel,
  PapiRequestDacheckoutCreditcardDelete,
  PapiRequestDacheckoutSubscriptionsCancel,
} from '@wix/da-papi-types';
import { BILLING_ORDERS_STREAM } from '../../constants';
import {
  deleteCard,
  removeCardFromList,
  cancelCore,
  removeCoreSubscription,
  fetchNextOrders,
  cancelMemberSubscription,
  removeMemberSubscription,
  viewSubscriptionReceipt,
} from '../actions/billing';
import { getSubscriptionByOrderIdV3 } from '../selectors/billing';
import { PoniesModalType } from '../types/modals';
import { BiData, BiEvent, CancelSubscriptionConfirmBiEvent } from '@wix/da-bi/pkg/events';

const logger = ddt.createLogger('billingsaga');

function* deleteCardSaga(action: ReturnType<typeof deleteCard>) {
  if (!action.payload) {
    logger.error('Tried to unblock invalid cardId', action);
    return;
  }
  const { cardId } = action.payload;

  const data: PapiRequestDacheckoutCreditcardDelete = {
    storedcardid: cardId,
  };

  const response: XhrResponse<PapiDacheckoutCreditcardDelete> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/creditcard/delete',
      data,
    },
    () => ({ success: true }),
    'dacheckout'
  );

  if (!response || isApiError(response) || !response.success) {
    yield putErrorNotification(i18next.t('common.error.deleteCard'));
    return;
  }

  yield put(modalActions.popModal());
  yield put(removeCardFromList(cardId));
}

function* cancelCoreSaga(action: ReturnType<typeof cancelCore>) {
  if (!action.payload) {
    logger.error('Tried to cancel invalid orderId', action);
    return;
  }
  const { orderId } = action.payload;

  if (orderId) {
    const biContext: ReturnType<typeof getPageBiContext> = yield select(getPageBiContext);
    const event: BiEvent = BiData<CancelSubscriptionConfirmBiEvent>({
      evid: 527,
      ...biContext,
      orderid: orderId,
    });
    yield call(logBiEvent, event);
  }

  const data: PapiRequestDacheckoutSubscriptionsCancel = {
    orderid: orderId,
  };

  const response: XhrResponse<PapiDacheckoutSubscriptionsCancel> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/subscriptions/cancel',
      data,
    },
    () => {
      logger.info('cancelCoreSaga, fake response');
      return { subscription: null };
    },
    'dacheckout'
  );

  yield put(modalActions.popModal());

  if (!response || isApiError(response)) {
    logger.error('cancelCoreSaga', { response });
    yield putErrorNotification(i18next.t('common.error.cancelCore'));
    return;
  }

  yield put(removeCoreSubscription());

  const subscription = yield select(getSubscriptionByOrderIdV3, orderId);
  if (subscription) {
    yield put(
      pushModal(PoniesModalType.CANCEL_SUBSCRIPTION, {
        params: { subscription, isCancelComplete: true },
      })
    );
  }
}
function* cancelMemberSubscriptionSaga(action: ReturnType<typeof cancelMemberSubscription>) {
  if (!action.payload) {
    logger.error('Tried to cancel invalid orderId', action);
    return;
  }

  const { orderId, reason } = action.payload;
  const data: PapiRequestDacheckoutSubscriptionsCancel = { orderid: orderId };
  // convert form string to papi number value and validate
  if (reason !== undefined) {
    const parsed = Number.parseInt(reason, 10) | 0;
    if (parsed >= 1 && parsed <= 5) {
      data.reasonid = parsed as PapiRequestDacheckoutSubscriptionsCancel['reasonid'];
    }
  }

  if ('reason' in action.payload && action.payload.reason !== undefined) {
    const parsedReason = Number.parseInt(action.payload.reason, 10) | 0;
    if (parsedReason >= 1 && parsedReason <= 5) {
      data.reasonid = parsedReason as PapiRequestDacheckoutSubscriptionsCancel['reasonid'];
    }
  }

  const response: XhrResponse<PapiDacheckoutSubscriptionsCancel> = yield call(
    requestPuppy,
    {
      method: 'post',
      url: '/subscriptions/cancel',
      data,
    },
    () => ({ subscription: null }),
    'dacheckout'
  );

  yield put(modalActions.popModal());

  if (!response || isApiError(response)) {
    logger.error('cancelMemberSubscriptionSaga', { response });
    yield putErrorNotification(i18next.t('common.error.cancelSubscription'));
    return;
  }

  yield put(removeMemberSubscription(orderId));

  const subscription = yield select(getSubscriptionByOrderIdV3, orderId);

  if (subscription) {
    yield put(
      pushModal(PoniesModalType.CANCEL_SUBSCRIPTION, {
        params: { subscription, isCancelComplete: true },
      })
    );
  }
}

function* fetchMoreOrdersSaga(action: ReturnType<typeof fetchNext>) {
  const offset = yield select(withOffset.selectors.getNextOffset, BILLING_ORDERS_STREAM);

  const response: XhrResponse<PapiDacheckoutOrders> = yield call(
    requestPuppy,
    {
      method: 'get',
      url: '/orders',
      params: { offset, limit: 24 },
    },
    undefined,
    'dacheckout'
  );

  if (!response || isApiError(response)) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId: BILLING_ORDERS_STREAM,
        fetchType: FetchType.Next,
      })
    );
    yield putErrorNotification(i18next.t('common.error.fetchMoreOrders'));
    return;
  }

  yield put(
    withOffset.actions.fetchSuccess({
      streamId: BILLING_ORDERS_STREAM,
      hasMore: response.hasMore ?? false,
      items: response.resultsV3 ?? [],
      nextOffset: response.nextOffset ?? undefined,
    })
  );
}

function* viewSubscriptionReceiptSaga(action: ReturnType<typeof viewSubscriptionReceipt>) {
  const { subscription } = action.payload;

  if (subscription.orderV3) {
    yield put(
      pushModal(PoniesModalType.ORDER_DETAILS, {
        params: { order: subscription.orderV3 },
      })
    );
  }
}

export default function* saga(): SagaIterator {
  yield all([
    takeLatest(deleteCard, deleteCardSaga),
    takeLatest(cancelCore, cancelCoreSaga),
    takeLatest(cancelMemberSubscription, cancelMemberSubscriptionSaga),
    takeLatest(fetchNextOrders, fetchMoreOrdersSaga),
    takeLatest(viewSubscriptionReceipt, viewSubscriptionReceiptSaga),
  ]);
}
